/* eslint react-hooks/exhaustive-deps: 0 */ // --> OFF

import {
  Button,
  Checkbox2,
  FormCard,
  MessageTile,
  Select,
  Text,
  TextField,
} from "@suralink/samurai/ui";
import { Alert, Error, Warning } from "@suralink/samurai/icons";
import { SuraLogoWithText } from "@suralink/samurai/visuals";
import { connect } from "react-redux";
import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { RouteComponentProps } from "react-router-dom";
import styles from "../styles/Login.module.scss";
import ChatIcon from "../icons/Chat";
import axios from "../utils/axios";
import { getQueryParamsFromUrl } from "../utils/helpers";
import classNames from "classnames";
import * as actions from "../redux/actions";
import BrandingContent from "../components/shared/BrandingContent/BrandingContent";
import { MfaBypassTokenDuration } from "../enums/mfaBypassTokens";

interface ComponentProps {}

interface StateProps {
  passwordSaved: boolean;
  onMfaStep: boolean;
  userErrState: boolean;
  loginErrorState: {
    message: string;
    status: number;
  };
  resendVerificationEmailStatus: number;
  reactivateAccountStatus: number;
}

interface DispatchProps {
  toggleUserErrorAction: (userErrState: boolean) => any;
  loginFailureAction: (err: any) => any;
  loginSuccessAction: (res: any) => any;
  toggleMfaAction: (show: boolean) => any;
  clearErrorStateAction: () => any;
}

type Props = StateProps & DispatchProps & ComponentProps & RouteComponentProps;

type MessageLocation = "above-logo" | "above-inputs";

const useReCAPTCHA = process.env.REACT_APP_USE_RECAPTCHA !== "false";

function MfaRecoveryContent({ goToSignIn }: any) {
  return (
    <div className={styles.mfaRecoveryContent}>
      <Text type="H2" className={styles.mfaRecoveryHeader}>
        Lost access to your authenticator?
      </Text>
      <div className={styles.mfaRecoverySubtext}>
        <Text type="Small2">
          Ask a firm administrator to send you a recovery passcode on the team
          dashboard.
        </Text>
        <Text type="Small2" className={styles.mfaRecoverySubtext2}>
          You can also contact Suralink live chat support or send an email
          to&nbsp;
          <span
            className={styles.mfaRecoverySupportLink}
            onClick={() =>
              (window.location.href =
                "mailto:support@suralink.com?subject=Mfa Recovery Token")
            }
          >
            support@suralink.com
          </span>
        </Text>
      </div>
      <Button
        color="blue"
        onClick={goToSignIn}
        label="Go to sign in"
        className={styles.mfaRecoveryContentButton}
      />
    </div>
  );
}

let mfaTimeout: any;

const Login: React.FC<Props> = ({
  passwordSaved,
  onMfaStep,
  userErrState,
  toggleUserErrorAction,
  loginFailureAction,
  loginSuccessAction,
  toggleMfaAction,
  loginErrorState,
  clearErrorStateAction,
  resendVerificationEmailStatus,
  reactivateAccountStatus,
  history,
}) => {
  const [showMfaRecoveryContent, setShowMfaRecoveryContent] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [mfaCode, setMfaCode] = useState("");
  const [displayMessage, setDisplayMessage] = useState("");
  const [messageLocation, setMessageLocation] = useState<MessageLocation | "">(
    ""
  );
  const [subtext, setSubtext] = useState("");
  const [subsubtext, setSubsubtext] = useState("");
  const [alertType, setAlertType] = useState("");
  const [emailMissing, setEmailMissing] = useState(false);
  const [passwordMissing, setPasswordMissing] = useState(false);
  const [rememberMfaChecked, setRememberMfaChecked] = useState(false);
  const [rememberMfaDuration, setRememberMfaDuration] = useState(
    MfaBypassTokenDuration[0]
  );
  const [reCaptchaScriptAdded, setReCaptchaScriptAdded] = useState(false);

  const passwordInput = useRef<HTMLInputElement>(null);
  const mfaInput = useRef<HTMLInputElement>(null);
  const loginForm = useRef<HTMLFormElement>(null);

  const queryParams = getQueryParamsFromUrl(window.location.href);
  const {
    logout,
    sessionInvalid,
    inviteAccepted,
    inviteNotFound,
    emailVerified,
    coBrandingEnabled,
    clientId,
    redirectUri,
    state,
  } = queryParams;

  window.clientId = clientId;
  window.redirectUri = redirectUri;

  let firmLogo;
  try {
    const stateJson = JSON.parse(state);
    firmLogo = stateJson.firmLogo;
  } catch (e) {}

  const authUrl = process.env.REACT_APP_BACKEND_URL;
  const authRoute = `${authUrl}/oauth/token`;
  const validateRoute = `${authUrl}/oauth/login`;

  // if critical url params are missing send them to suralink.com
  useEffect(() => {
    if (!clientId || !redirectUri) {
      window.location.href = "https://www.suralink.com";
    }
    return;
  });

  // these will become depricated when error messages are sent from the backend
  useEffect(() => {
    if (logout) {
      setUserMessage(
        "warning",
        "You have been logged out.",
        "You may be redirected to re-authenticate with your provider.",
        "",
        "above-logo"
      );
    }

    if (sessionInvalid) {
      setUserMessage(
        "warning",
        "You've logged into the system on another browser or device.",
        "If you did not log in on another device, please alert Suralink support or your Firm Administrator immediately",
        "",
        "above-logo"
      );
    }

    if (inviteAccepted) {
      setUserMessage(
        "alert",
        "Your invitation has already been accepted. Please sign in.",
        "",
        "",
        "above-logo"
      );
    }

    if (inviteNotFound) {
      setUserMessage(
        "error",
        "We could not locate your invitation. Please contact your firm administrator.",
        "",
        "",
        "above-logo"
      );
    }

    if (emailVerified) {
      setUserMessage(
        "alert",
        "Your account has already been verified. Please sign in.",
        "",
        "",
        "above-logo"
      );
    }
  }, [logout, sessionInvalid, inviteAccepted, inviteNotFound, emailVerified]);

  useEffect(() => {
    if (resendVerificationEmailStatus === 409) {
      setUserMessage(
        "alert",
        "Your account has already been verified. Please sign in.",
        "",
        "",
        "above-logo"
      );
    }
  }, [resendVerificationEmailStatus]);

  useEffect(() => {
    if (reactivateAccountStatus === 204) {
      setUserMessage(
        "alert",
        "Your account has been reactivated!",
        "Please sign in.",
        "",
        "above-logo"
      );
    }
    if (reactivateAccountStatus === 500) {
      setUserMessage(
        "error",
        "Something went wrong while reactivating your account.",
        "Please try again later, or contact Suralink support for assistance.",
        "",
        "above-logo"
      );
    }
  }, [reactivateAccountStatus]);

  useEffect(() => {
    switch (loginErrorState.status) {
      case 500:
      case 400: {
        setUserMessage(
          "error",
          "An unexpected error occurred.",
          "If the issue persists, contact Suralink Support.",
          "",
          "above-logo"
        );
        break;
      }
      case 403: {
        if (loginErrorState.message === "User email has not been verified") {
          history.push(
            `/create-account/verify${history.location.search}&email=${email}`
          );
        } else {
          let message =
            loginErrorState.message || "Username or password is incorrect.";
          let subtext = "";
          if (!!message.match(/(MFA)/)) {
            const attemptsMatch = message.match(/\d/);
            let remainingAttempts = attemptsMatch
              ? parseInt(attemptsMatch[0])
              : NaN;

            message = "Passcode was entered incorrectly.";

            if (!isNaN(remainingAttempts)) {
              subtext = `You have ${remainingAttempts} more attempt${
                remainingAttempts > 1 ? "s" : ""
              }.`;
            }

            resetFromMfaStep(true);
          }
          setUserMessage("error", message, subtext, "", "above-inputs");
        }
        break;
      }
      case 423: {
        if (onMfaStep) {
          resetFromMfaStep();
        }
        if (!!loginErrorState.message.match(/(Account expired)/)) {
          setUserMessage(
            "warning",
            "It's been a while since you've been here.",
            "To keep your data secure, we've inactivated your account.",
            "Please check your email for a reactivation link to log in.",
            "above-logo"
          );
          break;
        }
        setUserMessage(
          "error",
          "Your account has been temporarily locked.",
          "Please try again later, or contact Suralink support for assistance.",
          "",
          "above-logo"
        );
        break;
      }
      case 401: {
        clearErrorStateAction();
        toggleMfaAction(true);

        mfaTimeout = setTimeout(() => {
          resetFromMfaStep();
          loginFailureAction({
            data: {
              message: "Code entry time expired, please log in again.",
            },
            status: 1,
          });
        }, 120000);

        if (mfaInput.current) {
          mfaInput.current.focus();
        }
        break;
      }
      case 1: {
        setUserMessage(
          "error",
          "Code entry time expired, please log in again.",
          "",
          "",
          "above-logo"
        );
      }
    }
  }, [loginErrorState]);

  useEffect(() => {
    return () => {
      // clear mfa timeout on unmount, in case of user navigation
      if (mfaTimeout) clearTimeout(mfaTimeout);
    };
  }, []);

  useEffect(() => {
    if (!reCaptchaScriptAdded && useReCAPTCHA) {
      var script = document.createElement("script");
      script.type = "text/javascript";
      script.src = `https://www.google.com/recaptcha/api.js?render=${process.env.REACT_APP_RECAPTCHA_KEY}`;
      document.body.appendChild(script);
      setReCaptchaScriptAdded(true);
    }
  });

  async function login(
    e: React.FormEvent<HTMLFormElement>,
    reCaptchaToken: string
  ): Promise<void> {
    e.preventDefault();

    const formValid = checkForm();
    if (!formValid) return;

    try {
      // figure out which mfa data to send, if any
      let mfaData: any = {};
      if (onMfaStep) mfaData = { mfaCode };
      if (onMfaStep && rememberMfaChecked) {
        mfaData.mfaBypassDuration =
          MfaBypassTokenDuration[rememberMfaDuration as any];
      }

      let loginData = {
        email,
        password,
        ...mfaData,
        clientId,
        redirectUri,
      };
      if (useReCAPTCHA) {
        loginData.reCaptchaToken = reCaptchaToken;
      }

      await axios({
        method: "POST",
        url: validateRoute,
        data: loginData,
        withCredentials: true,
      })
        .then((res) => {
          if (loginForm && loginForm.current) {
            loginForm.current.submit();
            loginSuccessAction(res);
          }
        })
        .catch((err) => {
          loginFailureAction(err.response);
        });
    } catch (err) {
      throw err;
    }
  }

  const setUserMessage = (
    type: string,
    message: string,
    subtext: string,
    subsubtext: string,
    location: MessageLocation = "above-inputs"
  ) => {
    setDisplayMessage(message);
    setSubtext(subtext);
    setSubsubtext(subsubtext);
    setAlertType(type);
    setMessageLocation(location);
    toggleUserErrorAction(true);
  };

  const resetFromMfaStep = (inputOnly?: boolean) => {
    if (inputOnly && passwordInput.current) {
      passwordInput.current.focus();
    }
    setMfaCode("");
    setRememberMfaChecked(false);
    if (!inputOnly) {
      setPassword("");
      toggleMfaAction(false);
    }
  };

  const mfaInputIsValid =
    typeof mfaCode === "string" &&
    (mfaCode.length === 6 || mfaCode.length === 8);
  const credentialInputIsValid =
    typeof email === "string" &&
    email.length > 0 &&
    typeof password === "string" &&
    password.length > 0;

  const checkForm = () => {
    if (onMfaStep && !mfaInputIsValid) {
      setUserMessage(
        "error",
        "6 digit security code required.",
        "",
        "",
        "above-inputs"
      );
      toggleUserErrorAction(true);
      setPasswordMissing(true);
      return false;
    }
    if (!credentialInputIsValid) {
      setUserMessage(
        "error",
        "Username and password are required.",
        "",
        "",
        "above-inputs"
      );
      toggleUserErrorAction(true);
      setEmailMissing(true);
      setPasswordMissing(true);
      return false;
    }
    return true;
  };

  const handleSignIn = async (e: any, reCaptchaToken: string) => {
    try {
      await login(e, reCaptchaToken);
    } catch (err: any) {
      throw new Error(err.message);
    }
  };

  // handle when user clicks 'get a recovery token' on mfa step
  function handleClickGetMfaRecoveryToken() {
    setShowMfaRecoveryContent(true);
    resetFromMfaStep();
    if (mfaTimeout) clearTimeout(mfaTimeout);
  }

  const handleOpenChat = () => {
    const chatUrl = `https://v2.zopim.com/widget/livechat.html?key=2AekYJAlcSZCOOwpwwxkRyIkGeHIDfty&api_calls=[["concierge.setName",["Suralink Support"]],["concierge.setTitle",["Suralink Support"]],["theme.reload",[]],["theme.reload",[]]]&hostname=${window.location.href}&lang=en`;
    window.open(
      chatUrl,
      "_blank",
      "location=yes,height=570,width=440,scrollbar=yes,status=yes"
    );
  };

  return (
    <div className={styles.main}>
      <div className={styles.login}>
        <div className={styles.marketing}>
          <BrandingContent />
        </div>
        <div className={styles.inputModal}>
          <form action={authRoute} method="POST" ref={loginForm} noValidate>
            <FormCard
              style={{
                height: "auto",
                minHeight: "650px",
                margin: 0,
                padding: `${
                  coBrandingEnabled ? "36px 40px 24px 40px" : "45px"
                }`,
                borderRadius: "0 6px 6px 0",
                border: "none",
              }}
            >
              {false && (
                <MessageTile
                  state="error"
                  message="Suralink is experiencing issues."
                  subtext="We are investigating an issue that is preventing users from logging into our system."
                  subsubtext="Please check https://status.suralink.com for updates."
                  style={{
                    position: "relative",
                    top: `${coBrandingEnabled ? "-13px" : "-30px"}`,
                  }}
                  icon={<Error />}
                  dismissAlertCb={() => null}
                />
              )}
              {userErrState && messageLocation === "above-logo" ? (
                <MessageTile
                  state={alertType}
                  message={displayMessage}
                  subtext={subtext || null}
                  subsubtext={subsubtext || null}
                  style={{
                    position: "relative",
                    top: `${coBrandingEnabled ? "-13px" : "-30px"}`,
                  }}
                  icon={
                    alertType === "warning" ? (
                      <Warning />
                    ) : alertType === "alert" ? (
                      <Alert />
                    ) : alertType === "error" ? (
                      <Error />
                    ) : null
                  }
                  dismissAlertCb={() => {
                    setUserMessage("", "", "", "");
                    setEmailMissing(false);
                    setPasswordMissing(false);
                    toggleUserErrorAction(false);
                  }}
                />
              ) : null}
              <div className={styles.icon}>
                <SuraLogoWithText />
              </div>
              <hr
                className={classNames({
                  [styles.hrMarginSmall]: coBrandingEnabled,
                  [styles.hrMarginPass]: passwordSaved,
                  [styles.hrMfaRecoveryContentVisible]: showMfaRecoveryContent,
                })}
              />
              {showMfaRecoveryContent && (
                <MfaRecoveryContent
                  goToSignIn={() => setShowMfaRecoveryContent(false)}
                />
              )}
              {!showMfaRecoveryContent && (
                <React.Fragment>
                  {onMfaStep && (
                    <Text type="H2" className={styles.mfaHeader}>
                      Two-factor passcode required
                    </Text>
                  )}
                  {passwordSaved && !userErrState ? (
                    <div className={styles.passwordSuccess}>
                      <h3>New password saved!</h3>
                      <p>Please sign in to your account.</p>
                      <hr style={{ margin: "24px auto 56px auto" }} />
                    </div>
                  ) : null}
                  {coBrandingEnabled && firmLogo ? (
                    <div className={styles.coBranding}>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "center",
                        }}
                      >
                        <img
                          src={firmLogo}
                          alt=""
                          width={328}
                          style={{
                            maxHeight: "168px",
                            maxWidth: "428px",
                            height: "auto",
                            width: "auto",
                          }}
                        />
                      </div>
                    </div>
                  ) : null}
                  {userErrState && messageLocation === "above-inputs" ? (
                    <MessageTile
                      state={alertType}
                      message={displayMessage}
                      subtext={subtext || null}
                      subsubtext={subsubtext || null}
                      style={{
                        width: "428px",
                        margin: "auto",
                      }}
                      icon={
                        alertType === "warning" ? (
                          <Warning />
                        ) : alertType === "alert" ? (
                          <Alert />
                        ) : alertType === "error" ? (
                          <Error />
                        ) : null
                      }
                      dismissAlertCb={() => {
                        setUserMessage("", "", "", "");
                        setEmailMissing(false);
                        setPasswordMissing(false);
                        toggleUserErrorAction(false);
                      }}
                    />
                  ) : null}

                  <TextField
                    placeholder="email address"
                    label="Email address"
                    required
                    showRequiredState={
                      !onMfaStep &&
                      (emailMissing || loginErrorState.status === 403) &&
                      userErrState
                    }
                    requiredText=""
                    onChange={(e: any) => {
                      setEmail(e.target.value.trim());
                      clearErrorStateAction();
                    }}
                    onBlur={() => {
                      setEmailMissing(false);
                    }}
                    locked={onMfaStep}
                    style={{
                      width: "428px",
                      margin: "24px auto 32px",
                    }}
                    type="email"
                    name="email"
                    value={email}
                  />
                  {onMfaStep ? (
                    <React.Fragment>
                      <TextField
                        placeholder={""}
                        label={"Security passcode"}
                        type={"text"}
                        ref={mfaInput}
                        iconType={"searchIcon"}
                        required
                        showRequiredState={
                          (passwordMissing || loginErrorState.status === 403) &&
                          userErrState
                        }
                        requiredText=""
                        onChange={(e: any) => setMfaCode(e.target.value.trim())}
                        onBlur={() => setPasswordMissing(false)}
                        style={{
                          width: "428px",
                          margin: "24px auto 32px",
                        }}
                        name={"mfaCode"}
                        value={mfaCode}
                      />
                      <div
                        style={{
                          marginTop: "-24px",
                        }}
                      >
                        <a
                          href="//suralink.zendesk.com/hc/en-us/sections/4423647909261-Two-Factor-Authentication"
                          target="_blank"
                          rel="noreferrer"
                          style={{
                            textAlign: "center",
                            display: "block",
                            fontSize: "12px",
                            fontWeight: "bold",
                          }}
                        >
                          Two-Factor Authentication Information
                        </a>
                      </div>
                    </React.Fragment>
                  ) : (
                    <TextField
                      placeholder={"password"}
                      label={"Password"}
                      type={"password"}
                      ref={passwordInput}
                      iconType={null}
                      required
                      showRequiredState={
                        (passwordMissing || loginErrorState.status === 403) &&
                        userErrState
                      }
                      requiredText=""
                      onChange={(e: any) => {
                        setPassword(e.target.value);
                        clearErrorStateAction();
                      }}
                      onBlur={() => setPasswordMissing(false)}
                      style={{
                        width: "428px",
                        margin: "24px auto 32px",
                      }}
                      name={"password"}
                      value={password}
                    />
                  )}

                  {onMfaStep && (
                    <>
                      <input type="hidden" name="email" value={email} />
                      <input type="hidden" name="password" value={password} />
                      <div className={styles.rememberMfaSection}>
                        <Checkbox2
                          checked={rememberMfaChecked}
                          onClick={() =>
                            setRememberMfaChecked(!rememberMfaChecked)
                          }
                          label="Remember me on this computer"
                        />
                        {rememberMfaChecked && (
                          <React.Fragment>
                            <Select
                              className={styles.rememberMfaSelect}
                              label="Remember this computer for"
                              options={
                                // display all of the text values of the MfaBypassTokenDuration enum
                                Object.values(MfaBypassTokenDuration).filter(
                                  (value) => typeof value === "string"
                                )
                              }
                              selected={rememberMfaDuration}
                              onSelect={(opt: any) =>
                                setRememberMfaDuration(opt)
                              }
                              closeOnSelect
                            />
                            <Text
                              type="Caption"
                              className={styles.rememberMfaSelectCaption}
                            >
                              Only do this on computers you trust
                            </Text>
                          </React.Fragment>
                        )}
                      </div>
                    </>
                  )}
                  <input type="hidden" name="clientId" value={clientId!} />
                  <input
                    type="hidden"
                    name="redirectUri"
                    value={redirectUri!}
                  />
                  <input type="hidden" name="grantType" value="password" />

                  <Button
                    label="Sign in"
                    type="submit"
                    className={classNames("g-recaptcha", styles.loginBtn)}
                    color="blue"
                    disabled={
                      onMfaStep ? !mfaInputIsValid : !credentialInputIsValid
                    }
                    style={{ width: "428px", margin: "24px auto 24px auto" }}
                    onClick={(e: any) => {
                      e.preventDefault();
                      clearErrorStateAction();
                      if (checkForm()) {
                        if (!useReCAPTCHA) {
                          handleSignIn(e, "");
                        } else {
                          window.grecaptcha.ready(function () {
                            window.grecaptcha
                              .execute(process.env.REACT_APP_RECAPTCHA_KEY, {
                                action: "submit",
                              })
                              .then(function (token: string) {
                                handleSignIn(e, token);
                              });
                          });
                        }
                      }
                    }}
                  />
                  {onMfaStep ? (
                    <div className={styles.row}>
                      <Text type="Small2">
                        Having trouble logging in?&nbsp;
                      </Text>
                      <Text
                        type="Small2"
                        className={styles.getMfaRecoveryTokenText}
                        onClick={handleClickGetMfaRecoveryToken}
                      >
                        Get a recovery token.
                      </Text>
                    </div>
                  ) : (
                    <p className={styles.passwordReset}>
                      Forgot your password?{" "}
                      <span
                        onClick={() => {
                          clearErrorStateAction();
                          setEmail("");
                          setPassword("");
                        }}
                      >
                        <Link
                          to={{
                            pathname: "/login/reset",
                            state: {
                              clientId,
                              redirectUri,
                              state,
                              coBrandingEnabled,
                              search: history.location.search,
                            },
                          }}
                        >
                          Reset it here
                        </Link>
                      </span>
                    </p>
                  )}
                </React.Fragment>
              )}
              <hr
                style={{
                  margin: `${coBrandingEnabled ? "36px auto" : "40px auto"}`,
                }}
              />

              <div
                className={styles.btn}
                style={{
                  position: "relative",
                  top: `${coBrandingEnabled ? "8px" : 0}`,
                }}
                onClick={handleOpenChat}
              >
                Live chat support
                <span style={{ position: "relative", left: "8px", top: "1px" }}>
                  <ChatIcon fillColor="#2a5a8c" />
                </span>
              </div>
            </FormCard>
          </form>
        </div>
      </div>
    </div>
  );
};

const mstp = (state: InitialStateModel): StateProps => {
  const {
    passwordSaved,
    onMfaStep,
    userErrState,
    loginErrorState,
    resendVerificationEmailStatus,
    reactivateAccountStatus,
  } = state;
  return {
    passwordSaved,
    onMfaStep,
    userErrState,
    loginErrorState,
    resendVerificationEmailStatus,
    reactivateAccountStatus,
  };
};

const matp = ((): DispatchProps => {
  const {
    toggleUserErrorAction,
    loginFailureAction,
    loginSuccessAction,
    toggleMfaAction,
    clearErrorStateAction,
  } = actions;
  return {
    toggleUserErrorAction,
    loginSuccessAction,
    loginFailureAction,
    toggleMfaAction,
    clearErrorStateAction,
  };
})();

export default connect(mstp, matp)(Login);
